import React from 'react';
import { 
  ArrowRight, 
  Search, 
  Shield, 
  FileText,
  BarChart,
  Zap,
  Settings,
  CheckCircle,
  Clock,
  Target,
  Server,
  Database,
  Lock,
  LineChart,
  Network
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Audit = () => {
  const navigate = useNavigate();

  // Services d'audit disponibles
  const auditServices = [
    {
      icon: Server,
      title: "Audit Technique",
      description: "Évaluation complète de l'infrastructure technique",
      points: [
        "Architecture système",
        "Performance applicative",
        "Scalabilité & résilience",
        "Revue de code"
      ]
    },
    {
      icon: Shield,
      title: "Audit Sécurité",
      description: "Analyse approfondie de la sécurité des systèmes",
      points: [
        "Tests d'intrusion",
        "Analyse des vulnérabilités",
        "Conformité RGPD",
        "Plans de remédiation"
      ]
    },
    {
      icon: Settings,
      title: "Audit Process",
      description: "Optimisation des processus opérationnels",
      points: [
        "Workflow analysis",
        "Efficience opérationnelle",
        "Gouvernance IT",
        "Best practices"
      ]
    }
  ];

  // Processus d'audit
  const auditProcess = [
    {
      icon: Search,
      phase: "Phase 1",
      title: "Analyse Préliminaire",
      duration: "1-2 semaines",
      tasks: [
        "Collecte des documents",
        "Interviews des parties prenantes",
        "Définition du périmètre",
        "Planning détaillé"
      ]
    },
    {
      icon: FileText,
      phase: "Phase 2",
      title: "Audit & Évaluation",
      duration: "2-4 semaines",
      tasks: [
        "Tests techniques",
        "Analyse des données",
        "Benchmarking",
        "Identification des risques"
      ]
    },
    {
      icon: BarChart,
      phase: "Phase 3",
      title: "Restitution & Plan d'Action",
      duration: "1-2 semaines",
      tasks: [
        "Synthèse des findings",
        "Recommandations",
        "Plan de remédiation",
        "Présentation des résultats"
      ]
    }
  ];

  // Domaines d'expertise
  const expertise = [
    {
      icon: Server,
      title: "Infrastructure",
      description: "Architecture & performance"
    },
    {
      icon: Lock,
      title: "Cybersécurité",
      description: "Protection & conformité"
    },
    {
      icon: Database,
      title: "Data Management",
      description: "Qualité & gouvernance"
    },
    {
      icon: LineChart,
      title: "Performance",
      description: "Optimisation & KPIs"
    },
    {
      icon: Network,
      title: "Intégration",
      description: "APIs & connectivité"
    },
    {
      icon: Shield,
      title: "Conformité",
      description: "Normes & régulations"
    }
  ];

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] mb-6 text-sm">
            Audit des SI
          </div>
          
          <h1 className="text-3xl md:text-5xl font-bold mb-6">
            Optimisez la Performance
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
              de Vos Systèmes
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 mb-8">
            Une évaluation complète de vos systèmes d'information pour 
            identifier les opportunités d'amélioration et réduire les risques.
          </p>

          {/* Stats rapides */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            <StatBox icon={Target} value="360°" label="Analyse complète" />
            <StatBox icon={Clock} value="4-8" label="Semaines" />
            <StatBox icon={CheckCircle} value="100+" label="Audits réalisés" />
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Nos Services d'Audit</h2>
              <p className="text-lg text-white/70">
                Une approche méthodique pour une évaluation exhaustive
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {auditServices.map((service, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-8 border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-6">
                    <service.icon className="w-6 h-6 text-[#017D7E]" />
                  </div>

                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <p className="text-white/70 mb-6">{service.description}</p>

                  <ul className="space-y-3">
                    {service.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-white/70">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#017D7E] mt-2" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Notre
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                  {" "}Méthodologie
                </span>
              </h2>
              <p className="text-lg text-white/70">
                Un processus structuré en trois phases pour des résultats optimaux
              </p>
            </div>

            <div className="space-y-8">
              {auditProcess.map((step, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-6 md:p-8 border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[#017D7E]/10">
                        <step.icon className="w-6 h-6 text-[#017D7E]" />
                      </div>
                      <div>
                        <div className="text-[#017D7E] text-sm mb-1">{step.phase}</div>
                        <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                      </div>
                    </div>

                    <div className="md:ml-auto flex items-center">
                      <div className="px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] text-sm">
                        {step.duration}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {step.tasks.map((task, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-white/70">
                        <CheckCircle className="w-5 h-5 text-[#017D7E]" />
                        {task}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Expertise Section */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Domaines d'Expertise</h2>
              <p className="text-lg text-white/70">
                Une couverture complète des aspects techniques et fonctionnels
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {expertise.map((domain, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-6 text-center border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#017D7E]/10 flex items-center justify-center mx-auto mb-4">
                    <domain.icon className="w-6 h-6 text-[#017D7E]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{domain.title}</h3>
                  <p className="text-sm text-white/70">{domain.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Optimiser vos Systèmes ?
            </h2>
            <p className="text-lg text-white/70 mb-8">
              Nos experts sont à votre disposition pour réaliser un audit
              complet de vos systèmes d'information.
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] 
                          hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all">
                Demander un audit
              </button>
              
              <button
                onClick={() => navigate('/services')}
                className="px-8 py-4 rounded-lg border border-green-500/50 
                          hover:bg-[#017D7E]/10 text-white font-medium transition-all">
                Autres services
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant StatBox réutilisable
const StatBox = ({ icon: Icon, value, label }) => (
  <div className="bg-white/5 rounded-xl p-6 border border-white/10
                hover:border-green-500/50 transition-all duration-300">
    <Icon className="w-8 h-8 text-[#017D7E] mb-4 mx-auto" />
    <div className="text-2xl font-bold text-[#017D7E] mb-2">{value}</div>
    <div className="text-sm text-white/70">{label}</div>
  </div>
);

export default Audit;